import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getSurveyById } from '../../services/adminService';
import { SubmittedSurvey } from '../../types/admin';
import { ReportViewModal } from '../ReportViewModal';
import './SurveyDetailView.css';
import { getRiskLevelClass, getRiskLevelText } from '../../utils/surveyUtils';

export const SurveyDetailView: React.FC = () => {
	const { surveyId } = useParams<{ surveyId: string }>();
	const { token } = useAuth();
	const navigate = useNavigate();
	const [survey, setSurvey] = useState<SubmittedSurvey | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>('');
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);

	const loadSurvey = useCallback(async () => {
		if (!token || !surveyId) return;

		try {
			setLoading(true);
			setError('');
			const data = await getSurveyById(token, surveyId);
			setSurvey(data);
		} catch (err) {
			setError('Greška pri učitavanju upitnika');
			console.error('Survey detail error:', err);
		} finally {
			setLoading(false);
		}
	}, [token, surveyId]);

	useEffect(() => {
		if (!surveyId) {
			navigate('/admin');
			return;
		}
		loadSurvey();
	}, [surveyId, navigate, loadSurvey]);

	const handleViewReport = () => {
		setIsReportModalOpen(true);
	};

	const handleCloseReportModal = () => {
		setIsReportModalOpen(false);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('sr-RS', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const formatAnswer = (answer: any): string => {
		if (typeof answer === 'boolean') {
			return answer ? 'Da' : 'Ne';
		}
		if (typeof answer === 'number') {
			return answer.toString();
		}
		return String(answer || 'Nema odgovora');
	};

	const groupAnswersBySection = () => {
		if (!survey?.answers) return {};

		const grouped: { [section: string]: typeof survey.answers } = {};

		survey.answers.forEach((answer) => {
			const section = answer?.question?.section?.title || 'Ostalo';
			if (!grouped[section]) {
				grouped[section] = [];
			}
			grouped[section].push(answer);
		});

		return grouped;
	};

	if (loading) {
		return (
			<div className='survey-detail-loading'>
				<p>Učitavanje upitnika...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className='survey-detail-error'>
				<p>{error}</p>
				<div className='error-actions'>
					<button onClick={loadSurvey} className='btn btn-primary'>
						Pokušaj ponovo
					</button>
					<Link to='/admin' className='btn btn-outline'>
						Nazad na dashboard
					</Link>
				</div>
			</div>
		);
	}

	if (!survey) {
		return (
			<div className='survey-detail-error'>
				<p>Upitnik nije pronađen</p>
				<Link to='/admin' className='btn btn-primary'>
					Nazad na dashboard
				</Link>
			</div>
		);
	}

	const groupedAnswers = groupAnswersBySection();

	const getTargetGroupText = (targetGroup: 'kids' | 'health-workers' | 'adults') => {
		const targetGroupMap = {
			kids: 'Deca',
			'health-workers': 'Zdravstveni radnici',
			adults: 'Odrasli',
		};
		return targetGroupMap[targetGroup] || 'Nepoznato';
	};

	return (
		<div className='survey-detail'>
			<div className='survey-detail-header'>
				<div className='header-navigation'>
					<Link to='/admin' className='back-link'>
						← Nazad na dashboard
					</Link>
					<Link to='/admin/surveys' className='back-link'>
						Svi upitnici
					</Link>
				</div>

				<div className='survey-info-card'>
					<div className='survey-main-info'>
						<h1>Detalji upitnika</h1>
						<div className='survey-meta'>
							<span className='meta-item'>📅 {formatDate(survey.submittedAt)}</span>
							<span className='meta-item'>👥 {getTargetGroupText(survey.targetGroup)}</span>
							{!!survey.age && <span className='meta-item'>🎂 {survey.age} godina</span>}
						</div>
					</div>

					<div className='risk-assessment'>
						<div className={`risk-score ${getRiskLevelClass(survey.riskLevel)}`}>
							<div className='risk-label'>{getRiskLevelText(survey.riskLevel)}</div>
						</div>
					</div>
				</div>
			</div>

			{/* Related Report */}
			{survey.report && (
				<div className='related-report'>
					<h2>Informacije o prijavi</h2>
					<div className='report-card'>
						<div className=''>
							{/* <span className='report-date'>{formatDate(survey.report.submittedAt)}</span> */}
							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Prihvaćena:</label>
									<span className='field-value'>{survey.report?.decision == null ? 'Na čekanju' : survey.report?.decision ? 'Da' : 'Ne'}</span>
								</div>
								<div className='form-group-view'>
									<label>Osoba pristala da bude korisnik centra:</label>
									<span className='field-value'>{survey.report?.enrolled == null ? '-' : survey.report?.enrolled ? 'Da' : 'Ne'}</span>
								</div>
							</div>

							<div className='report-form-row'>
								<div className='form-group-view'>
									<label>Obradio:</label>
									<span className='field-value'>{survey.report?.completedBy || '-'}</span>
								</div>
								<div className='form-group-view'>
									<label>Razlog:</label>
									<span className='field-value'>{survey.report?.reason ?? '-'}</span>
								</div>
							</div>
						</div>
						<button onClick={handleViewReport} className='btn btn-primary btn-sm'>
							Prikaži prijavu
						</button>
					</div>
				</div>
			)}

			{/* Survey Answers */}
			<div className='survey-answers'>
				<h2>Odgovori na pitanja</h2>

				{Object.keys(groupedAnswers).length === 0 ? (
					<div className='no-answers'>
						<p>Nema zabeleženih pozitivnih odgovora</p>
					</div>
				) : (
					<div className='answers-sections'>
						{Object.entries(groupedAnswers).map(([section, answers]) => (
							<div key={section} className='answers-section'>
								<h3 className='section-title'>{section}</h3>
								<div className='answers-list'>
									{answers.map((answer, index) => (
										<div key={answer.question?.id || index} className='answer-item'>
											<div className='question-text'>{answer.question?.title || `${answer}`}</div>
											<div className='answer-value italic' style={{ color: '#666' }}>
												({answer.question?.score})
											</div>
											<div className='answer-value' style={{ fontWeight: answer.answer ? 'bold' : 500, color: answer.answer ? 'red' : '#666' }}>
												{formatAnswer(answer.answer)}
											</div>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Report View Modal */}
			{survey?.report && <ReportViewModal isOpen={isReportModalOpen} onClose={handleCloseReportModal} reportData={survey.report.formData} surveyId={survey.report.surveyId} report={survey.report} riskScore={survey.report.riskScore} timestamp={survey.report.submittedAt} />}
		</div>
	);
};
